import { useToast } from 'components/app/NotificationArea'
import Button from 'components/base/Button'
import Checkbox from 'components/base/Checkbox'
import FormItem from 'components/base/FormItem'
import Input from 'components/base/Input'
import PhoneNumberInput from 'components/base/PhoneNumberInput'
import Captcha from 'components/base/Captcha'
import { captchaStatusAtom } from 'components/auth/lib/atoms'
import Container from 'components/landing/Container'
import { FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { CompanyContactRequest } from 'lib/dto'
import { useIntl } from 'lib/intl-utils'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { useForm, Controller } from 'react-hook-form'

const contentByLang = {
  ru: {
    heading: 'Давайте обсудим сотрудничество',
    description: 'Расскажем, как мы можем помочь вашей компании',
    privacyPrefix: 'Продолжая, вы соглашаетесь с',
    privacyLabel: 'политикой обработки персональных данных',
  },
  en: {
    heading: "Let's work together",
    description: "Let's discuss how we can help your company",
    privacyPrefix: 'By continuing, you agree to our',
    privacyLabel: 'personal data processing policy',
  },
} as const

type Props = {
  heading?: string
  description?: string
  source?: CompanyContactRequest['source']
}

const DemoContactForm: FC<Props> = ({ heading, description, source = 'main' }) => {
  const ctx = useUserContext()
  const { t, lang } = useIntl()
  const captchaStatus = useAtomValue(captchaStatusAtom)
  const [captchaVisible, setCaptchaVisible] = useState(false)
  const showToast = useToast()
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
    control,
  } = useForm<CompanyContactRequest>({ mode: 'onChange' })

  const formRef = useRef<HTMLFormElement>(null)

  const content = contentByLang[lang as 'en' | 'ru']

  const onDemoFormSubmit = useCallback(
    async (data: CompanyContactRequest) => {
      const { agreeToProcessingPersonalData, ...rest } = data
      try {
        const response = await ctx.requestDemo({ ...rest, source } as CompanyContactRequest)

        if (response.status === 'success') {
          showToast('success', t`landing.we_will_contact_you`)
          reset()
          return
        }
        showToast('error', t`landing.something_went_wrong`)
      } catch {
        showToast('error', t`landing.something_went_wrong`)
      }
    },
    [ctx, reset, showToast, t, source]
  )

  useEffect(() => {
    const form = formRef.current

    if (!form) return

    form.style.setProperty('--gradient-angle', '0')

    const listener = (event: MouseEvent) => {
      const rect = form.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const dx = event.clientX - centerX
      const dy = event.clientY - centerY

      const angle = Math.atan2(dy, dx) - Math.PI / 2

      form.style.setProperty('--gradient-angle', `${angle}rad`)
    }

    document.addEventListener('mousemove', listener)

    return () => document.removeEventListener('mousemove', listener)
  }, [formRef.current])

  return (
    <Container className="relative sm:!pb-36 !pt-12">
      <Captcha visible={captchaVisible} />
      <a id="access" className="absolute -translate-y-8" />
      <div className="max-w-xl mx-auto lg:max-w-2xl" onFocus={() => setCaptchaVisible(true)}>
        <h2 className="text-4xl font-semibold tracking-tight text-gray-900">{heading ?? content.heading}</h2>
        <p className="mt-2 text-lg leading-8 text-gray-600">{description ?? content.description}</p>
        <div className="flex flex-col gap-16 mt-16 sm:gap-y-20 lg:flex-row">
          <form
            ref={formRef}
            onSubmit={handleSubmit(onDemoFormSubmit)}
            className="px-6 sm:px-12 z-20 py-12 border-2 max-w-2xl [background:linear-gradient(white,white)_padding-box,linear-gradient(var(--gradient-angle),#fd4113_0,#fea912_20%,transparent_80%)_border-box;] supports-[color:oklch(0_0_0)]:[background:linear-gradient(white,white)_padding-box,linear-gradient(var(--gradient-angle),oklch(67.3%_0.273_33.6)_0,oklch(81.5%_0.193_72.5)_20%,transparent_80%)_border-box;] shadow-lg border-transparent lg:flex-auto h-fit rounded-xl"
          >
            <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
              <FormItem label={t`name`} error={errors.name?.message}>
                <Input
                  {...register('name', { required: t`required_field` })}
                  type="text"
                  autoComplete="name"
                  aria-label={t`name`}
                  hasError={Boolean(errors.name)}
                />
              </FormItem>
              <FormItem label={t`company`}>
                <Input {...register('company')} type="text" autoComplete="organization" aria-label={t`company`} />
              </FormItem>
              <FormItem label="Email" error={errors.email?.message}>
                <Input
                  {...register('email', {
                    required: t`required_field`,
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t`registration.invalid_email` },
                  })}
                  type="email"
                  autoComplete="email"
                  aria-label="Email"
                  hasError={Boolean(errors.email)}
                />
              </FormItem>
              <FormItem label={t`phone`} error={errors.phone?.message}>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: t`required_field`,
                    validate: (value?: string | null) =>
                      !value || isPossiblePhoneNumber(value) || t`registration.invalid_phone`,
                  }}
                  render={({ field }) => (
                    <PhoneNumberInput
                      {...field}
                      ref={field.ref}
                      value={field.value ?? null}
                      aria-label={t`phone`}
                      hasError={Boolean(errors.phone?.message)}
                    />
                  )}
                />
              </FormItem>
              <label className="flex items-center sm:col-span-2 gap-2">
                <Controller
                  name="agreeToProcessingPersonalData"
                  control={control}
                  defaultValue={false}
                  rules={{ required: true }}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Checkbox {...field} checked={value} onChange={onChange} />
                  )}
                />

                <span className="translate-y-px">
                  {lang === 'ru' && (
                    <>
                      Я даю своё{' '}
                      <a
                        href="/doc/sellematics/ru/personal_data_consent.pdf"
                        className="font-medium text-primary-600"
                      >
                        согласие
                      </a>{' '}
                      на обработку моих персональных данных
                    </>
                  )}
                  {lang === 'en' && (
                    <>
                      I give my{' '}
                      <a
                        href="/doc/sellematics/ru/personal_data_consent.pdf"
                        className="font-medium text-primary-600"
                      >
                        consent
                      </a>{' '}
                      to the processing of my personal data
                    </>
                  )}
                </span>
              </label>
              <div className="text-xs text-gray-500 -mt-4 sm:col-span-2">
                {content.privacyPrefix}{' '}
                <a href="/doc/sellematics/ru/privacy_policy.pdf" className="text-primary-600">
                  {content.privacyLabel}
                </a>
              </div>
            </div>
            <Button
              type="submit"
              theme="primary"
              disabled={!isValid || !captchaStatus.passed || isSubmitting}
              className="justify-center w-full mt-10"
            >
              {t`landing.contact_me`}
            </Button>
          </form>
        </div>
      </div>
      <div
        className="absolute hidden sm:block inset-x-0 top-[calc(100%-13rem)] z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-45rem)]"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff5a39] to-[#fcc63c] opacity-20 sm:left-[calc(50%+12rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </Container>
  )
}

export default DemoContactForm
